import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, Star } from "lucide-react";
import KineticLines, { EASE } from "@/components/KineticLines";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import SectionHead from "@/components/SectionHead";
import CTABand from "@/components/CTABand";
import { BtnPrimary, BtnGhost } from "@/components/Btn";
import { TechLabel } from "@/components/Bits";
import usePageMeta from "@/hooks/usePageMeta";
import { COMPANY, SERVICES, CATALOG, MANIFESTO, IMG } from "@/config/company";

export default function Home() {
  usePageMeta(
    "MMC S.r.l. — Carpenteria Metallica Bergamo | Competenza, qualità e affidabilità",
    "MMC S.r.l., carpenteria metallica in provincia di Bergamo: strutture in acciaio, carpenteria pesante e leggera e saldatura certificata. Richiedi un preventivo senza impegno."
  );

  const heroRef = useRef(null);
  const spotRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const { scrollYProgress: spotP } = useScroll({ target: spotRef, offset: ["start end", "end start"] });
  const spotY = useTransform(spotP, [0, 1], ["-8%", "8%"]);

  return (
    <main>
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-screen flex-col border-b border-white/10">
        <motion.div
          style={{ opacity: fade }}
          className="relative mx-auto grid w-full max-w-[1600px] flex-1 grid-cols-1 items-center gap-12 px-5 pb-14 pt-28 md:px-10 lg:grid-cols-12 lg:gap-8 lg:px-16 lg:pt-24"
        >
          <div className="relative z-10 lg:col-span-7">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.1 }}>
              <TechLabel className="text-molten">
                {COMPANY.name} — {COMPANY.sector}
              </TechLabel>
            </motion.div>
            <KineticLines
              className="mt-6 font-display text-[16.5vw] font-semibold uppercase leading-[0.92] tracking-tight sm:text-[12vw] lg:text-[6.2vw] xl:text-[7vw]"
              delay={0.25}
              stagger={0.14}
              lines={[
                <>Competenza<span className="text-molten">.</span></>,
                <>Qualità<span className="text-molten">.</span></>,
                <>Affidabilità<span className="text-molten">.</span></>,
              ]}
            />
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.9 }}
              className="mt-8 max-w-lg text-base leading-relaxed text-fog md:text-lg"
            >
              {COMPANY.intro}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 1.05 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <BtnPrimary to="/contatti" testid="hero-cta-preventivo">
                Richiedi un preventivo
              </BtnPrimary>
              <BtnGhost to="/servizi" testid="hero-cta-servizi">
                Scopri i nostri servizi
              </BtnGhost>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.3 }}
              data-testid="hero-google-rating"
              className="mt-7 inline-flex items-center gap-3 border border-white/10 bg-white/[0.03] px-4 py-3"
            >
              <Star className="size-4 shrink-0 text-molten" fill="currentColor" />
              <span className="font-tech text-[11px] uppercase tracking-[0.2em] text-white/70">
                {COMPANY.google.rating} su Google — {COMPANY.google.count} recensioni
              </span>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: EASE, delay: 0.5 }}
            className="relative lg:col-span-5"
          >
            <div className="clip-notch relative h-[58vw] max-h-[620px] overflow-hidden border border-white/10 sm:h-[440px] lg:h-[66vh]">
              <motion.img
                src={IMG.hero}
                alt="Officina di carpenteria metallica MMC: lavorazione dell'acciaio con scintille"
                style={{ y: imgY }}
                className="absolute inset-0 h-full w-full scale-[1.18] object-cover"
              />
              <div className="spotlight absolute inset-0" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
              <TechLabel className="absolute left-4 top-4">Fig. 01 — Officina MMC</TechLabel>
              <TechLabel className="absolute bottom-4 right-4 text-white/60">ACCIAO · LAMIERA · PROFILATI</TechLabel>
            </div>
            <div className="absolute -bottom-5 left-4 hidden border border-white/15 bg-steel/90 px-5 py-4 backdrop-blur md:block">
              <p className="font-tech text-[10px] tracking-[0.2em] text-white/60">MAT. S355J2H — CERT. 3.1</p>
              <p className="font-tech text-[10px] tracking-[0.2em] text-molten">STRUTTURE SU MISURA</p>
            </div>
          </motion.div>
        </motion.div>

        <div className="relative z-10 mx-auto flex w-full max-w-[1600px] items-center justify-between px-5 pb-8 md:px-10 lg:px-16">
          <TechLabel>Scroll per esplorare</TechLabel>
          <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
            <ArrowDown className="size-4 text-molten" />
          </motion.span>
        </div>

        <TechLabel className="absolute right-3 top-1/2 hidden -translate-y-1/2 rotate-90 xl:block">
          MMC — CARPENTERIA METALLICA
        </TechLabel>
      </section>

      <Marquee
        items={[
          "Carpenteria pesante",
          "Saldatura certificata",
          "Strutture in acciaio",
          "Metalliche su misura",
          "Scale e soppalchi",
          "Tettoie e coperture",
        ]}
        testid="home-marquee"
      />

      {/* MANIFESTO */}
      <section data-testid="home-manifesto" className="px-5 py-24 md:px-10 md:py-36 lg:px-16">
        <div className="mx-auto max-w-7xl">
          <SectionHead
            index="01"
            eyebrow="Il nostro metodo"
            title={"Tre parole.\nUn solo impegno."}
            sub={COMPANY.claim}
          />
          <div className="border-t border-white/10">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.05}>
                <div className="group grid grid-cols-1 gap-4 border-b border-white/10 py-10 transition-colors duration-300 hover:bg-white/[0.02] md:grid-cols-12 md:items-baseline md:gap-6 md:py-14">
                  <span className="font-tech text-sm text-molten md:col-span-2">/{m.n}</span>
                  <h3 className="font-display text-4xl font-semibold uppercase tracking-tight transition-transform duration-300 group-hover:translate-x-2 sm:text-5xl md:col-span-4">
                    {m.title}
                  </h3>
                  <p className="max-w-xl leading-relaxed text-fog md:col-span-6">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVIZI PREVIEW */}
      <section data-testid="home-servizi" className="border-y border-white/10 bg-steel px-5 py-24 md:px-10 md:py-32 lg:px-16">
        <div className="mx-auto max-w-7xl">
          <SectionHead
            index="02"
            eyebrow="Servizi"
            title="Dall'idea alla struttura"
            sub="Quattro lavorazioni, un unico metodo: precisione dal primo taglio all'ultima verniciatura."
          />
          <div className="mt-12 grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-2">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={(i % 2) * 0.08} className="h-full bg-steel">
                <Link to="/servizi" data-testid={`home-service-card-${s.id}`} className="group flex h-full flex-col">
                  <div className="relative h-56 overflow-hidden md:h-64">
                    <img
                      src={s.img}
                      alt={s.title}
                      loading="lazy"
                      className="h-full w-full object-cover grayscale transition-[filter,transform] duration-500 group-hover:scale-105 group-hover:grayscale-0"
                    />
                    <span className="absolute left-4 top-4 font-tech text-xs text-white/80">0{i + 1}</span>
                  </div>
                  <div className="flex flex-1 flex-col border-t border-white/10 p-6 md:p-8">
                    <h3 className="font-display text-2xl font-medium uppercase tracking-tight transition-colors duration-300 group-hover:text-molten md:text-3xl">
                      {s.title}
                    </h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-fog">{s.short}</p>
                    <span className="mt-5 inline-flex items-center gap-2 font-tech text-[10px] uppercase tracking-[0.25em] text-molten">
                      Dettagli
                      <ArrowRight className="size-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SPOTLIGHT */}
      <section ref={spotRef} data-testid="home-spotlight" className="relative h-[70vh] overflow-hidden border-b border-white/10">
        <motion.img
          src={IMG.sparksFactory}
          alt="Saldatore al lavoro nell'officina MMC"
          style={{ y: spotY }}
          className="absolute inset-0 h-full w-full scale-125 object-cover"
        />
        <div className="absolute inset-0 bg-ink/55" />
        <div className="spotlight absolute inset-0" />
        <div className="relative z-10 flex h-full items-center px-5 md:px-10 lg:px-16">
          <Reveal>
            <TechLabel className="text-molten">La nostra promessa</TechLabel>
            <p className="mt-6 max-w-3xl font-display text-3xl font-medium uppercase leading-tight sm:text-4xl lg:text-5xl">
              “{COMPANY.claim}”
            </p>
          </Reveal>
        </div>
      </section>

      {/* LAVORI PREVIEW */}
      <section data-testid="home-lavori" className="px-5 py-24 md:px-10 md:py-32 lg:px-16">
        <div className="mx-auto max-w-7xl">
          <SectionHead index="03" eyebrow="Lavori realizzati" title="Acciaio in opera" />
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
            {[CATALOG[0].items[0], CATALOG[2].items[0], CATALOG[3].items[0]].map((w, i) => (
              <Reveal key={w.id} delay={i * 0.1} className={i === 1 ? "md:mt-12" : i === 2 ? "md:mt-24" : ""}>
                <Link to="/lavori-realizzati" data-testid={`home-work-${w.id}`} className="group block">
                  <div className="relative h-[300px] overflow-hidden border border-white/10 md:h-[380px]">
                    <img
                      src={w.img}
                      alt={w.title}
                      loading="lazy"
                      className="h-full w-full object-cover grayscale transition-[filter,transform] duration-500 group-hover:scale-105 group-hover:grayscale-0"
                    />
                    <span className="absolute bottom-3 left-3 bg-ink/70 px-2 py-1 font-tech text-[10px] tracking-[0.25em] text-white/85 backdrop-blur">
                      {w.id}
                    </span>
                  </div>
                  <h3 className="mt-4 font-display text-xl font-medium uppercase tracking-tight transition-colors duration-300 group-hover:text-molten">
                    {w.title}
                  </h3>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-14">
            <BtnGhost to="/lavori-realizzati" testid="home-works-all-btn">
              Tutto il catalogo dei lavori
            </BtnGhost>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </main>
  );
}
